import React, { useState, useEffect, useCallback } from 'react';
import { GOAL_TYPES, GOAL_TYPE_INFO } from '../../services/StudyPlannerService';
import './GoalWizard.css';

/**
 * GoalWizard Component
 * 
 * Multi-step wizard for setting up candidate career goals.
 * Steps:
 * 1. Goal Type Selection
 * 2. Target Role & Skills
 * 3. Timeline & Availability
 * 4. Review & Confirm
 */

// Timeline options
const TIMELINE_OPTIONS = [
  { weeks: 4, label: '1 Month', description: 'Intensive focus' },
  { weeks: 8, label: '2 Months', description: 'Balanced pace' },
  { weeks: 12, label: '3 Months', description: 'Comprehensive learning' },
  { weeks: 24, label: '6 Months', description: 'Deep mastery' }
];

// Study hours options
const STUDY_HOURS_OPTIONS = [
  { hours: 1, label: '1 hour/day', description: 'Light commitment' },
  { hours: 2, label: '2 hours/day', description: 'Moderate pace' },
  { hours: 3, label: '3 hours/day', description: 'Dedicated learning' },
  { hours: 4, label: '4+ hours/day', description: 'Intensive mode' }
];

const GoalWizard = ({ 
  isOpen, 
  onClose, 
  onComplete, 
  candidateProfile,
  initialGoal = null,
  isFullPage = false
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Form state
  const [selectedGoalType, setSelectedGoalType] = useState(initialGoal?.goalType || null);
  const [targetRole, setTargetRole] = useState(initialGoal?.targetRole || '');
  const [targetSkills, setTargetSkills] = useState(initialGoal?.targetSkills || []);
  const [timelineWeeks, setTimelineWeeks] = useState(initialGoal?.timelineWeeks || 12);
  const [studyHoursPerDay, setStudyHoursPerDay] = useState(initialGoal?.studyHoursPerDay || 2);
  const [focusOnPractice, setFocusOnPractice] = useState(initialGoal?.focusOnPractice !== false);
  
  // Skill input
  const [skillInput, setSkillInput] = useState('');

  // Reset when opened
  useEffect(() => {
    if (isOpen && !initialGoal) {
      setCurrentStep(1);
      setSelectedGoalType(null);
      setTargetRole('');
      setTargetSkills([]);
      setTimelineWeeks(12);
      setStudyHoursPerDay(2);
      setFocusOnPractice(true);
    }
  }, [isOpen, initialGoal]);

  // Suggested role based on profile
  useEffect(() => {
    if (candidateProfile?.roleApplyFor && !targetRole) {
      setTargetRole(candidateProfile.roleApplyFor);
    }
  }, [candidateProfile, targetRole]);

  // Suggested skills based on profile
  useEffect(() => {
    if (candidateProfile?.keySkills && targetSkills.length === 0) {
      const skills = candidateProfile.keySkills.split(',').map(s => s.trim()).filter(Boolean);
      // Don't auto-select, but could use for suggestions
    }
  }, [candidateProfile, targetSkills]);

  const handleGoalTypeSelect = useCallback((goalType) => {
    setSelectedGoalType(goalType);
    // Auto-advance after selection with animation
    setTimeout(() => {
      goToStep(2);
    }, 300);
  }, []);

  const toggleSkill = useCallback((skill) => {
    setTargetSkills(prev => 
      prev.includes(skill) 
        ? prev.filter(s => s !== skill)
        : [...prev, skill]
    );
  }, []);

  // Add skill from input
  const addSkill = useCallback(() => {
    const skill = skillInput.trim();
    if (skill && !targetSkills.includes(skill)) {
      setTargetSkills(prev => [...prev, skill]);
      setSkillInput('');
    }
  }, [skillInput, targetSkills]);

  // Handle Enter key to add skill
  const handleSkillKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    }
  }, [addSkill]);

  // Remove skill
  const removeSkill = useCallback((skillToRemove) => {
    setTargetSkills(prev => prev.filter(s => s !== skillToRemove));
  }, []);

  const goToStep = useCallback((step) => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(step);
      setIsAnimating(false);
    }, 200);
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep < 4) {
      goToStep(currentStep + 1);
    }
  }, [currentStep, goToStep]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep]);

  const handleComplete = useCallback(() => {
    setShowConfetti(true);
    
    const goalData = {
      goalType: selectedGoalType,
      targetRole,
      targetSkills,
      timelineWeeks,
      studyHoursPerDay,
      focusOnPractice,
      createdAt: new Date().toISOString()
    };
    
    setTimeout(() => {
      onComplete(goalData);
      setShowConfetti(false);
    }, 1500);
  }, [selectedGoalType, targetRole, targetSkills, timelineWeeks, studyHoursPerDay, focusOnPractice, onComplete]);

  const canProceed = useCallback(() => {
    switch (currentStep) {
      case 1:
        return selectedGoalType !== null;
      case 2:
        return targetRole.trim() !== '' && targetSkills.length > 0;
      case 3:
        return timelineWeeks > 0 && studyHoursPerDay > 0;
      case 4:
        return true;
      default:
        return false;
    }
  }, [currentStep, selectedGoalType, targetRole, targetSkills, timelineWeeks, studyHoursPerDay]);

  if (!isOpen) return null;

  return (
    <div className={`goal-wizard-overlay ${isFullPage ? 'full-page' : ''}`}>
      {/* Animated Background */}
      <div className="wizard-animated-bg">
        <div className="bg-gradient-1"></div>
        <div className="bg-gradient-2"></div>
        <div className="bg-gradient-3"></div>
        <div className="floating-shapes">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`floating-shape shape-${i + 1}`}></div>
          ))}
        </div>
      </div>

      <div className={`goal-wizard-modal ${isFullPage ? 'full-page-modal' : ''}`}>
        {/* Success Animation */}
        {showConfetti && (
          <div className="success-animation">
            <div className="success-ring"></div>
            <div className="success-check">✓</div>
          </div>
        )}

        {/* Header */}
        <div className="goal-wizard-header">
          <div className="header-content">
            <div className="header-text">
              <h2>Set Your Career Goal</h2>
              <p>Create a personalized path to achieve your objectives</p>
            </div>
          </div>
          {!isFullPage && (
            <button className="close-button" onClick={onClose} aria-label="Close">
              <span className="close-x"></span>
            </button>
          )}
        </div>

        {/* Progress Indicator */}
        <div className="progress-indicator">
          {[1, 2, 3, 4].map((step) => (
            <div 
              key={step}
              className={`progress-step ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}
              onClick={() => step < currentStep && goToStep(step)}
            >
              <div className="step-circle">
                {currentStep > step ? '✓' : step}
              </div>
              <span className="step-label">
                {step === 1 && 'Goal'}
                {step === 2 && 'Skills'}
                {step === 3 && 'Timeline'}
                {step === 4 && 'Review'}
              </span>
            </div>
          ))}
          <div className="progress-line">
            <div 
              className="progress-fill" 
              style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
            />
          </div>
        </div>

        {/* Content Area */}
        <div className={`goal-wizard-content ${isAnimating ? 'animating' : ''}`}>
          
          {/* Step 1: Goal Type Selection */}
          {currentStep === 1 && (
            <div className="step-content step-goal-type">
              <h3>What's your primary goal?</h3>
              <p className="step-description">Choose the goal that best describes what you want to achieve</p>
              
              <div className="goal-type-grid">
                {Object.entries(GOAL_TYPE_INFO).map(([type, info]) => {
                  return (
                    <div
                      key={type}
                      className={`goal-type-card ${selectedGoalType === type ? 'selected' : ''}`}
                      onClick={() => handleGoalTypeSelect(type)}
                      style={{ '--accent-color': info.color }}
                    >
                      <h4>{info.label}</h4>
                      <p>{info.description}</p>
                      {selectedGoalType === type && (
                        <div className="selected-indicator">✓</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Target Role & Skills */}
          {currentStep === 2 && (
            <div className="step-content step-skills">
              <h3>Define your target</h3>
              <p className="step-description">Tell us about the role and skills you want to master</p>
              
              <div className="target-role-section">
                <label htmlFor="targetRole">Target Role</label>
                <input
                  id="targetRole"
                  type="text"
                  value={targetRole}
                  onChange={(e) => setTargetRole(e.target.value)}
                  placeholder="e.g., Full Stack Developer, Data Scientist..."
                  className="target-role-input"
                />
              </div>

              <div className="skills-section">
                <label>Skills to Learn ({targetSkills.length} added)</label>
                
                <div className="skill-input-container">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={handleSkillKeyDown}
                    placeholder="Type a skill and press Enter or click Add..."
                    className="skill-input"
                  />
                  <button 
                    type="button"
                    className="add-skill-btn"
                    onClick={addSkill}
                    disabled={!skillInput.trim()}
                  >
                    + Add
                  </button>
                </div>
                
                <p className="skill-hint">
                  Add any skills you want to learn - programming languages, frameworks, tools, soft skills, etc.
                </p>

                {targetSkills.length > 0 && (
                  <div className="selected-skills">
                    {targetSkills.map(skill => (
                      <span key={skill} className="skill-tag selected" onClick={() => removeSkill(skill)}>
                        {skill} ×
                      </span>
                    ))}
                  </div>
                )}
                
                {targetSkills.length === 0 && (
                  <div className="no-skills-message">
                    <p>No skills added yet. Start typing above to add skills you want to learn.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Timeline & Availability */}
          {currentStep === 3 && (
            <div className="step-content step-timeline">
              <h3>Plan your schedule</h3>
              <p className="step-description">Set a realistic timeline based on your availability</p>
              
              <div className="timeline-section">
                <label>Target Timeline</label>
                <div className="option-grid">
                  {TIMELINE_OPTIONS.map(option => (
                    <div
                      key={option.weeks}
                      className={`option-card ${timelineWeeks === option.weeks ? 'selected' : ''}`}
                      onClick={() => setTimelineWeeks(option.weeks)}
                    >
                      <div className="option-value">{option.label}</div>
                      <div className="option-description">{option.description}</div>
                      {timelineWeeks === option.weeks && (
                        <div className="selected-check">✓</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="hours-section">
                <label>Daily Study Time</label>
                <div className="option-grid">
                  {STUDY_HOURS_OPTIONS.map(option => (
                    <div
                      key={option.hours}
                      className={`option-card ${studyHoursPerDay === option.hours ? 'selected' : ''}`}
                      onClick={() => setStudyHoursPerDay(option.hours)}
                    >
                      <div className="option-value">{option.label}</div>
                      <div className="option-description">{option.description}</div>
                      {studyHoursPerDay === option.hours && (
                        <div className="selected-check">✓</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="preference-section">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    checked={focusOnPractice}
                    onChange={(e) => setFocusOnPractice(e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <div className="checkbox-label">
                    Focus on hands-on practice
                    <span className="checkbox-hint">Recommended for faster skill acquisition</span>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Step 4: Review & Confirm */}
          {currentStep === 4 && (
            <div className="step-content step-review">
              <h3>Review your goal</h3>
              <p className="step-description">Make sure everything looks good before we create your learning path</p>
              
              <div className="review-card">
                <div className="review-section">
                  <div className="review-label">Goal</div>
                  <div className="review-value">
                    <span className="goal-badge" style={{ backgroundColor: GOAL_TYPE_INFO[selectedGoalType]?.color }}>
                      {GOAL_TYPE_INFO[selectedGoalType]?.label}
                    </span>
                  </div>
                </div>

                <div className="review-section">
                  <div className="review-label">Target Role</div>
                  <div className="review-value">{targetRole}</div>
                </div>

                <div className="review-section">
                  <div className="review-label">Skills to Master</div>
                  <div className="review-skills">
                    {targetSkills.map(skill => (
                      <span key={skill} className="review-skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>

                <div className="review-section">
                  <div className="review-label">Timeline</div>
                  <div className="review-value">
                    {TIMELINE_OPTIONS.find(t => t.weeks === timelineWeeks)?.label} 
                    <span className="review-hint">
                      ({studyHoursPerDay} hours/day)
                    </span>
                  </div>
                </div>

                <div className="review-section">
                  <div className="review-label">Estimated Completion</div>
                  <div className="review-value highlight">
                    {new Date(Date.now() + timelineWeeks * 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              </div>

              <div className="motivation-box">
                <p>
                  You're about to start an exciting journey. We'll create a personalized 
                  roadmap with curated resources and practice questions to help you achieve 
                  your goal.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer with Navigation */}
        <div className="goal-wizard-footer">
          <button 
            className="btn-back"
            onClick={handleBack}
            disabled={currentStep === 1}
          >
            ← Back
          </button>
          
          <div className="step-counter">
            Step {currentStep} of 4
          </div>
          
          {currentStep < 4 ? (
            <button 
              className="btn-next"
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Next →
            </button>
          ) : (
            <button 
              className="btn-complete"
              onClick={handleComplete}
              disabled={!canProceed()}
            >
              Create My Path
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoalWizard;
